import { CheckboxItem, PRIORITIZE_PAGE_CONTENT } from "@utils/constants";
import { getOptionValueList, matchOrCreateItems } from "@utils/helpers";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { MultiSelectItem } from "@fronterahealth/frontera-ui-components";

import {
  ReportPageLocationEnums,
  ReportTreatmentPlanPrioritizeFieldsInput,
  useAddUpdateReportTreatmentPlanPrioritizeFieldsMutation,
} from "@api/graphql/types-and-hooks";
import {
  InlineCheckboxListField,
  InlineMultiSelectField,
  InlineRadioField,
  InlineTextAreaField,
} from "@components/BlurBasedFormFields";
import { UseBlurBasedFormOptions, useBlurBasedForm } from "@components/BlurBasedFormFields/useBlurBasedForm";
import { ConfirmGenerationDialog } from "@components/ConfirmGenerationDialog/ConfirmGenerationDialogV2";
import { ContentRow } from "@components/ContentRow/ContentRow";
import { transformQueryData } from "@components/forms/utils";
import { notifyError } from "@components/notifications/notifications";
import { FooterButtonRow } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/FooterButtonRow";
import { useReport } from "@providers/ReportProvider";

export const Prioritize: React.FC = () => {
  const { report, reportQuery } = useReport();

  const { refetch } = reportQuery;

  const { assessmentId: reportId } = useParams();
  const [promptDialogOpen, setPromptDialogOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  /** Only allow click-outside autosave after we've hydrated from server; avoids overwriting server data on initial load */
  const hasHydratedRef = useRef<boolean>(false);

  const [familyLife, setFamilyLife] = useState<MultiSelectItem[]>([]);
  const [engagementAbility, setEngagementAbility] = useState<boolean>(false);
  const [communicationAbility, setCommunicationAbility] = useState<string[]>([]);
  const [socialDesires, setSocialDesires] = useState<string>("");

  const addUpdatePrioritizeMutation = useAddUpdateReportTreatmentPlanPrioritizeFieldsMutation({});
  const {
    communicationAbility: communicationAbilityContent,
    familyLife: familyLifeContent,
    engagementAbility: engagementAbilityContent,
    socialDesires: socialDesiresContent,
  } = PRIORITIZE_PAGE_CONTENT;

  /** Build API payload for only the given override keys (single-field update). */
  const buildPartialPayload = useCallback(
    (
      overrides: Partial<ReportTreatmentPlanPrioritizeFieldsInput>,
    ): Partial<ReportTreatmentPlanPrioritizeFieldsInput> => {
      const payload: Partial<ReportTreatmentPlanPrioritizeFieldsInput> = {};
      if (overrides.familyLife !== undefined) {
        payload.familyLife = getOptionValueList(overrides.familyLife as unknown as MultiSelectItem[]);
      }
      if (overrides.engagementAbility !== undefined) {
        const v = overrides.engagementAbility as boolean | string | undefined;
        payload.engagementAbility = v === true || v === "yes";
      }
      if (overrides.communicationAbility !== undefined) {
        payload.communicationAbility = overrides.communicationAbility;
      }
      if (overrides.socialDesires !== undefined) {
        payload.socialDesires = overrides.socialDesires;
      }
      return payload;
    },
    [],
  );

  /** Build full API payload (all fields) for click-outside or Next. */
  const buildFullPayload = useCallback((): ReportTreatmentPlanPrioritizeFieldsInput => {
    const existingSafetyConcerns = (report?.reportTreatmentPlanPrioritizeFields?.safetyConcerns ?? []).filter(
      (s): s is string => s != null,
    );
    return {
      familyLife: getOptionValueList(familyLife),
      engagementAbility,
      communicationAbility,
      safetyConcerns: existingSafetyConcerns,
      socialDesires,
    };
  }, [familyLife, engagementAbility, communicationAbility, socialDesires, report]);

  const savePrioritize = useCallback(
    async (overrides?: Partial<ReportTreatmentPlanPrioritizeFieldsInput>) => {
      const payload =
        overrides && Object.keys(overrides).length > 0 ? buildPartialPayload(overrides) : buildFullPayload();
      try {
        await addUpdatePrioritizeMutation.mutateAsync({
          reportId: reportId ? reportId : "missing-report-id",
          prioritizeFieldsDetails: payload as ReportTreatmentPlanPrioritizeFieldsInput,
        });
      } catch (error) {
        console.error("Error Saving Prioritize Page", error);
        throw error;
      }
    },
    [buildPartialPayload, buildFullPayload, reportId, addUpdatePrioritizeMutation],
  );

  const setters = {
    familyLife: setFamilyLife,
    engagementAbility: setEngagementAbility,
    communicationAbility: setCommunicationAbility,
    socialDesires: setSocialDesires,
  };

  const normalizeValue = <K extends keyof ReportTreatmentPlanPrioritizeFieldsInput>(
    key: K,
    value: unknown,
  ): ReportTreatmentPlanPrioritizeFieldsInput[K] => {
    switch (key) {
      case "engagementAbility":
        return (value === true || value === "yes") as ReportTreatmentPlanPrioritizeFieldsInput[K];

      case "familyLife":
        return (value ?? []) as ReportTreatmentPlanPrioritizeFieldsInput[K];

      case "communicationAbility":
        return (
          Array.isArray(value) ? value.filter((s): s is string => s != null) : []
        ) as ReportTreatmentPlanPrioritizeFieldsInput[K];

      case "socialDesires":
        return (value ?? "") as ReportTreatmentPlanPrioritizeFieldsInput[K];

      default:
        return value as ReportTreatmentPlanPrioritizeFieldsInput[K];
    }
  };

  const state = {
    familyLife,
    engagementAbility,
    communicationAbility,
    socialDesires,
  } as unknown as ReportTreatmentPlanPrioritizeFieldsInput;

  const { handleFieldSave } = useBlurBasedForm<ReportTreatmentPlanPrioritizeFieldsInput>({
    containerRef,
    mutationFn: savePrioritize,
    isSaveReady: () => hasHydratedRef.current,
    state,
    setters: setters as unknown as UseBlurBasedFormOptions<ReportTreatmentPlanPrioritizeFieldsInput>["setters"],
    normalizeValue,
    onError: () => {
      notifyError("Error Saving Prioritize Page");
      refetch();
    },
  });

  useEffect(() => {
    const converted = transformQueryData(report, {
      "reportTreatmentPlanPrioritizeFields.engagementAbility": (value) =>
        value === false || value === "no" ? "no" : "yes",
    });

    const fields = converted.reportTreatmentPlanPrioritizeFields;
    if (!fields) return;

    setFamilyLife(
      fields.familyLife
        ? matchOrCreateItems(fields.familyLife as string[], familyLifeContent.optionItems as MultiSelectItem[])
        : [],
    );
    setEngagementAbility((fields.engagementAbility as unknown) === "yes" || fields.engagementAbility === true);
    setCommunicationAbility((fields.communicationAbility ?? []).filter((s): s is string => s != null));
    setSocialDesires((fields.socialDesires as string) ?? "");
    hasHydratedRef.current = true;
  }, [report, familyLifeContent.optionItems]);

  const handleNext = useCallback(async () => {
    try {
      await savePrioritize();
      await refetch();
      setPromptDialogOpen(true);
    } catch {
      notifyError("Error Saving Prioritize Page");
    }
  }, [savePrioritize, refetch]);

  return (
    <>
      <ConfirmGenerationDialog
        type={ReportPageLocationEnums.TreatmentPlan}
        promptDialogOpen={promptDialogOpen}
        setPromptDialogOpen={setPromptDialogOpen}
      />
      <div className="flex w-full flex-col justify-start px-14 py-4">
        <div ref={containerRef} className="flex flex-col w-full gap-y-4">
          <ContentRow title={familyLifeContent.title} subtitle={familyLifeContent.description}>
            <InlineMultiSelectField<ReportTreatmentPlanPrioritizeFieldsInput>
              value={familyLife}
              fieldKey="familyLife"
              onSave={handleFieldSave}
              title={familyLifeContent.question}
              multiSelectItems={familyLifeContent.optionItems as MultiSelectItem[]}
              multiSelectMode="tags"
            />
          </ContentRow>
          <ContentRow title={engagementAbilityContent.title} subtitle={engagementAbilityContent.description}>
            <InlineRadioField<ReportTreatmentPlanPrioritizeFieldsInput>
              value={engagementAbility ? "yes" : "no"}
              fieldKey="engagementAbility"
              onSave={handleFieldSave}
              title={engagementAbilityContent.question}
              radioOptions={engagementAbilityContent.optionItems as { id: string; title: string }[]}
            />
          </ContentRow>
          <ContentRow title={communicationAbilityContent.title} subtitle={communicationAbilityContent.description}>
            <InlineCheckboxListField<ReportTreatmentPlanPrioritizeFieldsInput>
              value={communicationAbility}
              fieldKey="communicationAbility"
              onSave={handleFieldSave}
              title={communicationAbilityContent.question}
              checkboxItems={communicationAbilityContent.optionItems as CheckboxItem[]}
            />
          </ContentRow>
          <ContentRow title={socialDesiresContent.title} subtitle={socialDesiresContent.description}>
            <InlineTextAreaField<ReportTreatmentPlanPrioritizeFieldsInput>
              value={socialDesires}
              fieldKey="socialDesires"
              onSave={handleFieldSave}
              title={socialDesiresContent.question}
            />
          </ContentRow>
        </div>
      </div>
      <FooterButtonRow
        primaryButtonType="button"
        primaryButtonAction={handleNext}
        primaryButtonDisabled={addUpdatePrioritizeMutation.isPending || reportQuery.isFetching}
        primaryButtonLoading={reportQuery.isFetching}
        skipNavigate
      />
    </>
  );
};
